import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Calendar, Clock, Plus, Settings, BarChart, Shield,
  GripVertical, Trash2, Info, XCircle, X, Wand2, RefreshCw, Undo2, Redo2, AlertTriangle
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { calcWeekLabourHours, calcShiftHours } from '@/utils/labourHours';

export function AdminShiftsTab({ ctx }) {
  const {
    user, shifts, branches, selectedBranch, setSelectedBranch,
    selectedRoles, setSelectedRoles, systemRoles,
    filterFromTime, setFilterFromTime, weeklyReport, selectedWeek,
    changeWeek, getWeekDates, getISOWeekNumber,
    handleExportShiftsPDF, setShowCloneWeek, setShowCreateShift,
    setShowCreateDirectShift, openAssignDialog, handleEditShift,
    handleDeleteShift, handleUnassignUser, managerOnDuty, managersList, handleSetManagerOnDuty,
    handleShiftDragStart, handleShiftDragOver, handleShiftDrop, handleShiftDragEnd,
    draggedShift, handleBulkReorder,
    handleAutoSchedule, autoScheduling, autoScheduleResult, showAutoScheduleResult, setShowAutoScheduleResult,
    handleRemoveAutoSchedule, handleRefreshAutoSchedule,
    undoStack, redoStack, handleUndo, handleRedo,
    handleOverrideAssign, setAutoScheduleResult,
    unavailability,
  } = ctx;

  // Local drag state for smooth reordering
  const [localDayShifts, setLocalDayShifts] = useState({});
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const dragItem = useRef(null);

  const onDragStart = (e, shift, dayShifts, date) => {
    dragItem.current = { shift, date };
    setDraggingId(shift.id);
    setLocalDayShifts(prev => ({ ...prev, [date]: [...dayShifts] }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, targetShift, dayShifts, date) => {
    e.preventDefault();
    if (!dragItem.current || dragItem.current.date !== date) return;
    if (dragItem.current.shift.id === targetShift.id) return;

    setDragOverId(targetShift.id);

    // Optimistic reorder in local state
    const current = localDayShifts[date] || dayShifts;
    const dragIdx = current.findIndex(s => s.id === dragItem.current.shift.id);
    const targetIdx = current.findIndex(s => s.id === targetShift.id);
    if (dragIdx === -1 || targetIdx === -1 || dragIdx === targetIdx) return;

    const reordered = [...current];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    setLocalDayShifts(prev => ({ ...prev, [date]: reordered }));
  };

  const onDrop = async (e, date) => {
    e.preventDefault();
    if (!dragItem.current || dragItem.current.date !== date) return;

    const reordered = localDayShifts[date];
    if (reordered && handleBulkReorder) {
      const orderUpdates = reordered.map((s, idx) => ({ id: s.id, order: idx }));
      await handleBulkReorder(orderUpdates);
    }

    setDraggingId(null);
    setDragOverId(null);
    dragItem.current = null;
    setLocalDayShifts({});
  };

  const onDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
    dragItem.current = null;
    setLocalDayShifts({});
  };


  // Check if any shifts have auto-assigned users
  const hasAutoAssignments = shifts.some(s => s.assigned_users?.some(u => u.assigned_by === 'auto'));

  // Build unavailability lookup: user_id -> set of dates
  const unavailMap = React.useMemo(() => {
    const map = {};
    for (const u of (unavailability || [])) {
      const uid = u.user_id;
      if (!map[uid]) map[uid] = {};
      let d = new Date(u.date_from);
      const end = new Date(u.date_to);
      while (d <= end) {
        map[uid][d.toISOString().split('T')[0]] = u.reason + (u.hours ? ` (${u.hours}h)` : '');
        d.setDate(d.getDate() + 1);
      }
    }
    return map;
  }, [unavailability]);

  return (
    <div className="bg-white rounded-xl shadow-md border border-stone-200 p-4">
      {/* Header Row 1: Title and Actions */}
      <div className="flex justify-between items-start mb-2">
        <div>
          <h2 className="text-lg font-bold text-stone-800">Weekly Schedule</h2>
          <p className="text-xs text-stone-500">
            {selectedBranch} - Week {weeklyReport?.week_number || ''}, {weeklyReport?.year || new Date(selectedWeek).getFullYear()} | {new Date(selectedWeek).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(new Date(selectedWeek).setDate(new Date(selectedWeek).getDate() + 6)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </p>
          {(() => {
            const { available, allocated } = calcWeekLabourHours(shifts);
            const pct = available > 0 ? Math.round((allocated / available) * 100) : 0;
            const branch = branches?.find(b => b.name === selectedBranch);
            const costRate = branch?.avg_labour_cost_per_hour || 0;
            const allocatedCost = costRate > 0 ? (allocated * costRate) : 0;
            const availableCost = costRate > 0 ? (available * costRate) : 0;
            return (
              <div className="flex items-center gap-2 mt-1 flex-wrap" data-testid="week-labour-hours">
                <span className={`text-sm font-bold ${pct >= 80 ? 'text-green-700' : pct >= 50 ? 'text-orange-600' : 'text-stone-700'}`}>
                  {allocated}h / {available}h
                </span>
                <span className="text-[10px] text-stone-500">labour (excl. lunch)</span>
                <div className="w-24 h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-orange-400' : 'bg-stone-400'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
                <span className="text-[10px] font-medium text-stone-600">{pct}%</span>
                {costRate > 0 && (
                  <span className="text-xs font-semibold text-stone-700 ml-2 px-2 py-0.5 bg-stone-100 rounded" data-testid="week-labour-cost">
                    R{allocatedCost.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} / R{availableCost.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} cost
                  </span>
                )}
              </div>
            );
          })()}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => changeWeek(-1)} variant="outline" size="sm" data-testid="prev-week-button">
            ← Previous
          </Button>
          <Button onClick={() => changeWeek(1)} variant="outline" size="sm" data-testid="next-week-button">
            Next →
          </Button>
          {user.role === 'admin' && (
            <>
              <Button
                onClick={() => setShowCloneWeek(true)}
                data-testid="clone-week-button"
                variant="outline"
                className="border-gray-600 text-gray-600 hover:bg-gray-50"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Clone Week
              </Button>
              <Button onClick={() => ctx.setShowApplyTemplateWeek(true)} data-testid="apply-template-week-button" variant="outline" className="border-gray-600 text-gray-600 hover:bg-gray-50">
                <Calendar className="w-4 h-4 mr-2" />
                Apply Template
              </Button>
              <Button onClick={() => setShowCreateDirectShift(true)} data-testid="create-direct-shift-button" className="bg-gradient-to-r from-gray-600 to-gray-700 hover:bg-gray-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Create Shift
              </Button>
              <Button onClick={handleAutoSchedule} disabled={autoScheduling} data-testid="auto-schedule-button" className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white">
                <Wand2 className="w-4 h-4 mr-2" />
                {autoScheduling ? 'Scheduling...' : 'Auto Schedule'}
              </Button>
              {hasAutoAssignments && (
                <>
                  <Button onClick={handleRefreshAutoSchedule} disabled={autoScheduling} data-testid="refresh-auto-schedule-button" variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" title="Re-run auto-schedule (keeps manual assignments)">
                    <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                  </Button>
                  <Button onClick={handleRemoveAutoSchedule} disabled={autoScheduling} data-testid="remove-auto-schedule-button" variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50" title="Remove auto-scheduled assignments">
                    <Undo2 className="w-4 h-4 mr-1" /> Undo Auto
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Header Row 2: Filters */}
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-stone-200 flex-wrap">
        <Button
          onClick={handleExportShiftsPDF}
          data-testid="export-shifts-pdf-button"
          className="bg-red-600 hover:bg-red-700 text-white"
          size="sm"
        >
          <BarChart className="w-4 h-4 mr-2" />
          Export PDF
        </Button>

        {/* Undo / Redo */}
        <div className="flex items-center gap-1 ml-1">
          <Button onClick={handleUndo} disabled={!undoStack?.length} variant="outline" size="sm" data-testid="undo-button" title="Undo last action" className="px-2">
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button onClick={handleRedo} disabled={!redoStack?.length} variant="outline" size="sm" data-testid="redo-button" title="Redo last action" className="px-2">
            <Redo2 className="w-4 h-4" />
          </Button>
        </div>

        {/* Branch Filter */}
        <Select value={selectedBranch} onValueChange={setSelectedBranch}>
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {branches.map(branch => (
              <SelectItem key={branch.id} value={branch.name}>{branch.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Role Filter (Multi-select) */}
        <div className="relative">
          <Select
            value={selectedRoles.length > 0 ? "filtered" : "all"}
            onValueChange={() => {}}
          >
            <SelectTrigger className="w-48">
              <SelectValue>
                {selectedRoles.length === 0
                  ? "All Roles"
                  : `${selectedRoles.length} Role${selectedRoles.length > 1 ? 's' : ''} Selected`}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <div className="p-2">
                <div
                  onClick={() => setSelectedRoles([])}
                  className="px-2 py-1.5 text-sm cursor-pointer hover:bg-stone-100 rounded"
                >
                  <input type="checkbox" checked={selectedRoles.length === 0} readOnly className="mr-2" />
                  All Roles
                </div>
                {systemRoles.map(role => (
                  <div
                    key={role.id}
                    onClick={() => {
                      setSelectedRoles(prev =>
                        prev.includes(role.role_name)
                          ? prev.filter(r => r !== role.role_name)
                          : [...prev, role.role_name]
                      );
                    }}
                    className="px-2 py-1.5 text-sm cursor-pointer hover:bg-stone-100 rounded"
                  >
                    <input type="checkbox" checked={selectedRoles.includes(role.role_name)} readOnly className="mr-2" />
                    {role.role_name}
                  </div>
                ))}
              </div>
            </SelectContent>
          </Select>
        </div>

        {/* Time Filter */}
        <div className="flex items-center gap-2 border border-stone-200 rounded-lg px-3 py-1.5 bg-white">
          <Clock className="w-4 h-4 text-stone-400" />
          <span className="text-xs text-stone-500">From:</span>
          <Input
            type="time"
            value={filterFromTime}
            onChange={(e) => setFilterFromTime(e.target.value)}
            className="w-28 h-7 text-sm border-0 p-1 focus-visible:ring-0"
            data-testid="filter-from-time"
          />
          {filterFromTime && (
            <button onClick={() => setFilterFromTime('')} className="text-stone-400 hover:text-stone-600" title="Clear time filter">
              <XCircle className="w-4 h-4" />
            </button>
          )}
          <div className="relative group">
            <Info className="w-4 h-4 text-stone-400 cursor-help" />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-stone-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
              Shows shifts that end after this time
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-stone-800"></div>
            </div>
          </div>
        </div>

        <div className="flex-grow"></div>

        {/* Manager on Duty */}
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
          <Shield className="w-4 h-4 text-blue-600" />
          <span className="text-xs font-medium text-blue-700">Weekend Manager on Duty:</span>
          <Select
            value={managerOnDuty?.manager_id || "none"}
            onValueChange={(value) => handleSetManagerOnDuty(value === "none" ? null : value)}
          >
            <SelectTrigger className="w-40 h-7 text-xs bg-white">
              <SelectValue placeholder="Select Manager" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Not Assigned</SelectItem>
              {managersList.map(manager => (
                <SelectItem key={manager.id} value={manager.id}>
                  {manager.username} ({manager.branches?.join(', ') || manager.branch || 'No branch'})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {getWeekDates().map((date, idx) => {
          const baseDayShifts = shifts.filter(s => {
            const dateMatch = s.date === date;
            const roleMatch = selectedRoles.length === 0 || selectedRoles.includes(s.role_name);
            let timeMatch = true;
            if (filterFromTime) {
              timeMatch = s.end_time > filterFromTime;
            }
            return dateMatch && roleMatch && timeMatch;
          });
          const dayShifts = localDayShifts[date] || baseDayShifts;
          const dayName = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx];
          const branchColor = branches.find(b => b.name === selectedBranch)?.color || '#808080';
          const dayTotalHours = dayShifts.reduce((sum, s) => {
            const h = calcShiftHours(s.start_time, s.end_time, s.lunch_minutes || 0);
            return sum + h * (s.assigned_users?.length || 0);
          }, 0);
          const dayAvailableHours = dayShifts.reduce((sum, s) => {
            const h = calcShiftHours(s.start_time, s.end_time, s.lunch_minutes || 0);
            return sum + h * (s.max_capacity || 1);
          }, 0);

          return (
            <div
              key={date}
              className="border border-stone-200 rounded-md p-1.5 bg-stone-50 min-h-[80px]"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(e, date)}
            >
              <div className="flex items-baseline justify-between mb-1">
                <span className="font-semibold text-xs text-stone-800">{dayName}</span>
                <div className="text-right">
                  <span className="text-[10px] text-stone-500">{new Date(date).getDate()}</span>
                  {dayAvailableHours > 0 && (
                    <div className="text-[10px] font-medium text-emerald-700" data-testid={`day-hours-${idx}`}>
                      {Math.round(dayTotalHours * 10) / 10}/{Math.round(dayAvailableHours * 10) / 10}h
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                {dayShifts.map(shift => (
                  <div
                    key={shift.id}
                    draggable={user.role === 'admin'}
                    onDragStart={(e) => onDragStart(e, shift, baseDayShifts, date)}
                    onDragOver={(e) => onDragOver(e, shift, baseDayShifts, date)}
                    onDragEnd={onDragEnd}
                    data-testid={`shift-card-${shift.shift_code}`}
                    style={{ borderLeftColor: branchColor, borderLeftWidth: '3px' }}
                    className={`p-1.5 rounded text-xs hover:shadow-md transition-all duration-150 relative group ${
                      user.role === 'admin' ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                    } ${
                      shift.status === 'assigned'
                        ? 'bg-green-100 border border-green-300 text-green-800'
                        : shift.status === 'requested'
                        ? 'bg-yellow-100 border border-yellow-300 text-yellow-800'
                        : 'bg-white border border-stone-300 text-stone-700'
                    } ${
                      draggingId === shift.id ? 'opacity-40 scale-95' : ''
                    } ${
                      dragOverId === shift.id ? 'ring-2 ring-blue-400 ring-offset-1' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        {user.role === 'admin' && (
                          <div className="float-left mr-1 mt-0.5">
                            <GripVertical className="w-3 h-3 text-stone-400" />
                          </div>
                        )}
                        <div className="font-semibold cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); handleEditShift(shift); }}>{shift.shift_code}</div>
                        <div className="text-xs cursor-pointer" onClick={(e) => { e.stopPropagation(); handleEditShift(shift); }}>{shift.start_time}-{shift.end_time}</div>
                        {(() => {
                          const assigned = shift.assigned_users?.length || (shift.assigned_username ? 1 : 0);
                          const cap = shift.max_capacity || 1;
                          const hasRoom = assigned < cap;
                          return (
                            <>
                              <div className={`text-xs mt-1 font-semibold ${assigned >= cap ? 'text-green-700' : 'text-stone-500'}`}>
                                {assigned}/{cap}
                              </div>
                              {shift.assigned_users && shift.assigned_users.length > 0 ? (
                                <div className="text-xs font-medium mt-0.5 space-y-0.5 overflow-hidden max-w-full">
                                  {shift.assigned_users.map(u => {
                                    const unavailReason = unavailMap[u.user_id]?.[shift.date];
                                    return (
                                    <div key={u.user_id} className="flex items-center gap-1 group/name">
                                      {u.assigned_by === 'auto' && <Wand2 className="w-2.5 h-2.5 text-emerald-500 flex-shrink-0" title="Auto-scheduled" />}
                                      {unavailReason && <AlertTriangle className="w-2.5 h-2.5 text-amber-500 flex-shrink-0" title={`Unavailable: ${unavailReason}`} />}
                                      <span className={`truncate leading-tight flex-1 ${unavailReason ? 'text-amber-700' : ''}`}>{u.username}</span>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleUnassignUser(shift.id, u.user_id); }}
                                        className="flex-shrink-0 w-3.5 h-3.5 rounded-full bg-red-200 hover:bg-red-400 text-red-700 hover:text-white flex items-center justify-center opacity-0 group-hover/name:opacity-100 transition-opacity"
                                        title={`Remove ${u.username}`}
                                        data-testid={`remove-user-${u.user_id}`}
                                      >
                                        <X className="w-2.5 h-2.5" />
                                      </button>
                                    </div>
                                    );
                                  })}
                                </div>
                              ) : shift.assigned_username ? (
                                <div className="text-xs font-medium truncate">{shift.assigned_username}</div>
                              ) : null}
                              {hasRoom && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); openAssignDialog(shift); }}
                                  className="mt-1 w-full flex items-center justify-center gap-0.5 py-0.5 rounded border border-dashed border-stone-300 hover:border-blue-400 hover:bg-blue-50 text-stone-400 hover:text-blue-600 transition-colors"
                                  data-testid={`add-staff-${shift.id}`}
                                  title="Assign staff"
                                >
                                  <Plus className="w-3 h-3" />
                                  <span className="text-[10px]">Assign</span>
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    {user.role === 'admin' && (
                      <button
                        onClick={(e) => handleDeleteShift(shift.id, e)}
                        data-testid={`delete-shift-${shift.id}`}
                        className="absolute top-1 right-1 p-1 rounded bg-red-100 hover:bg-red-200 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete shift"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Auto Schedule Results Dialog */}
      <Dialog open={showAutoScheduleResult} onOpenChange={setShowAutoScheduleResult}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-emerald-600" /> Auto Schedule Results
            </DialogTitle>
          </DialogHeader>
          {autoScheduleResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-700">{autoScheduleResult.assigned_count}</div>
                  <div className="text-xs text-emerald-600">Staff Assigned</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700">{autoScheduleResult.unfilled_count}</div>
                  <div className="text-xs text-amber-600">Still Open</div>
                </div>
                <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-stone-700">{autoScheduleResult.skipped_no_candidates}</div>
                  <div className="text-xs text-stone-600">No Candidates</div>
                </div>
              </div>
              {autoScheduleResult.details?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-stone-800 text-sm mb-2">Assignments Made</h3>
                  <div className="max-h-60 overflow-y-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-stone-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Branch</th>
                          <th className="px-3 py-2 text-left">Shift</th>
                          <th className="px-3 py-2 text-left">Time</th>
                          <th className="px-3 py-2 text-left">Assigned To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {autoScheduleResult.details.map((d, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                            <td className="px-3 py-1.5">{new Date(d.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                            <td className="px-3 py-1.5">{d.branch}</td>
                            <td className="px-3 py-1.5"><span className="px-1.5 py-0.5 bg-gray-200 rounded text-[10px] font-semibold">{d.shift_code}</span></td>
                            <td className="px-3 py-1.5">{d.time}</td>
                            <td className="px-3 py-1.5 font-medium">{d.assigned_to}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Override options for unfilled slots */}
              {autoScheduleResult.override_options?.filter(s => !s.filled && s.options?.length > 0).length > 0 && (
                <div>
                  <h3 className="font-semibold text-stone-800 text-sm mb-2">Fill Remaining Slots</h3>
                  <p className="text-xs text-stone-500 mb-2">Select a staff member to override the rule and fill the shift.</p>
                  <div className="max-h-72 overflow-y-auto space-y-3">
                    {autoScheduleResult.override_options.filter(s => !s.filled && s.options?.length > 0).map((slot, i) => (
                      <div key={i} className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold text-amber-800">
                            {new Date(slot.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                          <span className="px-1.5 py-0.5 bg-amber-200 rounded text-[10px] font-semibold text-amber-900">{slot.shift_code}</span>
                          <span className="text-xs text-amber-700">{slot.start_time}-{slot.end_time} @ {slot.branch}</span>
                        </div>
                        <div className="space-y-1">
                          {slot.options.map((opt, j) => (
                            <div key={j} className="flex items-center justify-between bg-white rounded px-2 py-1.5 border border-amber-100">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-stone-800">{opt.username}</span>
                                <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded font-medium">{opt.override_label}</span>
                              </div>
                              <Button size="sm" variant="outline"
                                className="h-6 text-xs px-2 border-amber-400 text-amber-800 hover:bg-amber-100"
                                data-testid={`override-assign-${slot.shift_id}-${opt.user_id}`}
                                onClick={() => handleOverrideAssign(slot.shift_id, opt.user_id)}>
                                Assign
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Show filled overrides */}
              {autoScheduleResult.override_options?.filter(s => s.filled).length > 0 && (
                <div className="space-y-1">
                  {autoScheduleResult.override_options.filter(s => s.filled).map((slot, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded px-2 py-1">
                      <span>Filled: {slot.shift_code} on {new Date(slot.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} → {slot.filled_by}</span>
                    </div>
                  ))}
                </div>
              )}
              <Button onClick={() => setShowAutoScheduleResult(false)} className="w-full" variant="outline">Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
